import { LIVE_STREAMS_SUBSCRIPTION } from "@/graphql/subscriptions/streamTeamSubscriptions";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery, useSubscription } from "@apollo/client";
import { Calendar, CalendarDays, Radio, Twitch, Users } from "lucide-react";
import { Link } from "react-router-dom";

const LIVE_STREAMS_QUERY = gql`
    query LiveStreams {
        liveStreams {
            id
            streamTeamMember {
                id
                player {
                    id
                    name
                    avatarUrl
                }
                twitchUsername
                twitchAuth {
                    profileImageUrl
                }
            }
            isLive
            streamTitle
            gameName
            viewerCount
        }
    }
`;

interface LiveStream {
    id: string;
    streamTeamMember: {
        id: string;
        player: {
            id: string;
            name: string;
            avatarUrl: string | null;
        };
        twitchUsername: string;
        twitchAuth?: {
            profileImageUrl: string;
        } | null;
    };
    isLive: boolean;
    streamTitle: string;
    gameName: string;
    viewerCount: number;
}

export default function StreamTeamHomePage() {
    const { data, refetch } = useQuery<{ liveStreams: LiveStream[] }>(
        LIVE_STREAMS_QUERY,
        {
            fetchPolicy: "cache-and-network",
        },
    );

    useSubscription(LIVE_STREAMS_SUBSCRIPTION, {
        onData: () => {
            refetch();
        },
    });

    const liveStreams = data?.liveStreams || [];
    return (
        <div className="mx-auto w-full px-4 py-8 sm:px-6 sm:py-12 lg:w-3/4">
            {/* Hero Section */}
            <div className="mb-8 flex flex-col items-center justify-center gap-6 text-center sm:mb-12 sm:flex-row sm:gap-8 lg:gap-12">
                <div className="flex shrink-0 justify-center">
                    <img
                        src="/vrx-rune-logo.svg"
                        alt="Ravenguard Logo"
                        className="h-70 md:h-80"
                    />
                </div>
                <div className="flex flex-col justify-center">
                    <h1 className="font-prviking text-brand mb-3 text-3xl sm:mb-4 sm:text-5xl lg:text-6xl">
                        {convertLetters("Welcome to the Rookery")}
                    </h1>
                    <h2 className="font-prviking text-brand mb-2 text-xl sm:mb-3 sm:text-3xl lg:text-4xl">
                        {convertLetters(
                            "The official home of the VANDERHEIM RAVENGUARD (VRX)",
                        )}
                    </h2>
                    <p className="font-garamond max-w-3xl text-base text-zinc-300 sm:text-lg lg:text-xl">
                        A curated collective of trusted creators who have earned
                        their wings and solidified themselves as allies of
                        VANDERHEIM. Each member is recognised and entrusted to
                        carry the standard of the village beyond its borders.
                    </p>
                </div>
            </div>

            {/* Feature Cards */}
            <div className="mb-8 grid grid-cols-1 gap-4 sm:mb-12 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
                <FeatureCard
                    icon={<Users className="h-12 w-12" />}
                    title="Members"
                    description="Meet the valued Members of the VRX."
                    linkTo="/team"
                    linkText="Members"
                />
                <FeatureCard
                    icon={<Radio className="h-12 w-12" />}
                    title="Live"
                    description="Check out who’s live right now and watch them all from the comfort of the Rookery."
                    linkTo="/live"
                    linkText="Live"
                />
                <FeatureCard
                    icon={<Calendar className="h-12 w-12" />}
                    title="Schedule"
                    description="Never miss a stream with the VRX schedule. Every stream. All in one place."
                    linkTo="/schedule"
                    linkText="Schedule"
                />
                <FeatureCard
                    icon={<CalendarDays className="h-12 w-12" />}
                    title="Events"
                    description="Explore all official RAVENGUARD events."
                    linkTo="/events"
                    linkText="Events"
                />
            </div>

            {/* Live Now Section */}
            <div className="border-brand_stream_team rounded-2xl border-2 bg-zinc-900 p-4 shadow-xl sm:p-6 lg:p-8">
                <h2 className="font-prviking text-brand mb-4 flex flex-wrap items-center gap-2 text-2xl sm:mb-6 sm:gap-3 sm:text-3xl lg:text-4xl">
                    <Twitch className="h-7 w-7 sm:h-9 sm:w-9 lg:h-10 lg:w-10" />
                    {convertLetters("Live Now")}
                    {liveStreams.length > 0 && (
                        <span className="font-garamond text-lg text-zinc-300 sm:ml-2 sm:text-xl lg:text-2xl">
                            ({liveStreams.length})
                        </span>
                    )}
                </h2>
                {liveStreams.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
                        {liveStreams.map((stream) => {
                            const avatarUrl =
                                stream.streamTeamMember.twitchAuth
                                    ?.profileImageUrl ||
                                stream.streamTeamMember.player.avatarUrl;

                            return (
                                <Link
                                    key={stream.id}
                                    to={`/live?username=${stream.streamTeamMember.twitchUsername}`}
                                    className="group rounded-lg border-2 border-zinc-700 bg-zinc-900 p-3 transition-all hover:border-red-500 sm:p-4"
                                >
                                    <div className="mb-2 flex items-center gap-2 sm:gap-3">
                                        {avatarUrl ? (
                                            <img
                                                src={avatarUrl}
                                                alt={
                                                    stream.streamTeamMember
                                                        .player.name
                                                }
                                                className="h-8 w-8 rounded-full border-2 border-yellow-500 object-cover sm:h-10 sm:w-10"
                                            />
                                        ) : (
                                            <div className="bg-brand_stream_team_gold flex h-8 w-8 items-center justify-center rounded-full text-base font-bold text-zinc-900 sm:h-10 sm:w-10 sm:text-lg">
                                                {stream.streamTeamMember.player.name
                                                    .charAt(0)
                                                    .toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-prviking text-brand truncate text-base sm:text-lg">
                                                {convertLetters(
                                                    stream.streamTeamMember
                                                        .player.name,
                                                )}
                                            </h3>
                                        </div>
                                        <span className="bg-brand_stream_team_gold flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-xs font-bold text-zinc-900">
                                            <span className="h-2 w-2 animate-pulse rounded-full bg-red-600"></span>
                                            LIVE
                                        </span>
                                    </div>
                                    <p className="font-garamond mb-1 line-clamp-2 text-sm font-bold text-zinc-200">
                                        {stream.streamTitle}
                                    </p>
                                    <div className="font-garamond flex items-center justify-between text-xs text-zinc-400">
                                        <span className="truncate pr-2">
                                            {stream.gameName}
                                        </span>
                                        <span className="shrink-0">
                                            {stream.viewerCount.toLocaleString()}{" "}
                                            viewers
                                        </span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                ) : (
                    <p className="font-garamond text-zinc-400">
                        No streams are currently live. Check back soon or view
                        our schedule!
                    </p>
                )}
            </div>
        </div>
    );
}

interface FeatureCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    linkTo: string;
    linkText: string;
}

function FeatureCard({
    icon,
    title,
    description,
    linkTo,
    linkText,
}: FeatureCardProps) {
    return (
        <div className="group border-brand_stream_team rounded-xl border-2 bg-zinc-900 p-4 shadow-xl transition-all hover:border-yellow-500 sm:p-6">
            <div className="text-brand mb-3 sm:mb-4">{icon}</div>
            <h3 className="font-prviking text-brand mb-2 text-xl sm:text-2xl">
                {convertLetters(title)}
            </h3>
            <p className="font-garamond mb-3 text-sm text-zinc-300 sm:mb-4 sm:text-base">
                {description}
            </p>
            <Link
                to={linkTo}
                className="bg-brand hover:bg-brand_stream_team_gold/90 font-garamond inline-block rounded-lg px-4 py-2 text-sm font-medium text-zinc-900 transition-all sm:px-6 sm:text-base"
            >
                {linkText}
            </Link>
        </div>
    );
}
